/**
 * textarea 高度自适应 hook
 * 管理 textarea 的自动高度调整和编辑态聚焦
 */
import { useEffect, RefObject } from 'react';
import { QA_INPUT_EDITOR_MIN_HEIGHT_PX } from '../constants';
import { isMentionEditorSurface } from '../mentionEditor/types';
import type { TInputSurface } from '../mentionShared/useMentionTrigger';

interface IUseTextareaAutoResizeParams {
  /** 输入表面 ref（textarea 或 MentionEditor） */
  textareaRef: RefObject<TInputSurface | null>;
  /** 编辑区域行容器 ref */
  editorRowRef: RefObject<HTMLDivElement>;
  /** skill 标签容器 ref */
  skillTagsRef: RefObject<HTMLDivElement>;
  /** 输入值（变化时触发重新计算） */
  inputValue: string;
  /** 最大高度字符串，如 '182px' */
  maxHeight: string;
  /** 编辑区最小高度字符串，如 '51px'（默认由外壳总高 125px 反推） */
  minHeight: string;
  /** placeholder 文本（变化时触发重新计算） */
  placeholderMessage: string;
  /** skill 标签布局信息 */
  skillTagsLayout: { paddingTop: number; lastLineWidth: number; };
  /** 高度变化回调 */
  onTextInputHeightChange?: (newHeight: number) => void;
  /** 是否处于编辑态 */
  isEdit?: boolean;
}

const useTextareaAutoResize = ({
  textareaRef,
  editorRowRef,
  skillTagsRef,
  inputValue,
  maxHeight,
  minHeight,
  placeholderMessage,
  skillTagsLayout,
  onTextInputHeightChange,
  isEdit,
}: IUseTextareaAutoResizeParams) => {
  const resolveEditorEl = (
    surface: TInputSurface | null,
  ): HTMLElement | null => {
    if (!surface) return null;
    if (isMentionEditorSurface(surface)) {
      return surface.getRootElement();
    }
    return surface;
  };

  // 自动调整高度
  useEffect(() => {
    const textArea = resolveEditorEl(textareaRef.current);
    if (!textArea) return;

    const maxH = parseInt(maxHeight, 10);
    // Skill 标签通过 paddingTop 给 textarea 让位。textarea 的 maxHeight 需要把
    // 这部分 padding 额外补进来，否则 Skill 多到 paddingTop 接近/超过 maxH 时，
    // 真正的可见输入区会被压为 0 —— 提示词和光标就会消失。
    const effectiveMaxH = maxH + (skillTagsLayout.paddingTop || 0);

    textArea.style.height = 'auto';

    const minTextH = parseInt(minHeight, 10) || QA_INPUT_EDITOR_MIN_HEIGHT_PX;
    const newHeight = Math.min(Math.max(textArea.scrollHeight, minTextH), effectiveMaxH);
    textArea.style.height = `${newHeight}px`;

    // 同步更新 editor-row 的 min-height
    if (editorRowRef.current) {
      const tagsHeight = skillTagsRef.current?.offsetHeight || 0;
      // eslint-disable-next-line no-param-reassign
      editorRowRef.current.style.minHeight = `${Math.max(newHeight, tagsHeight)}px`;
    }

    if (onTextInputHeightChange) onTextInputHeightChange(newHeight);
  }, [inputValue, maxHeight, minHeight, onTextInputHeightChange, placeholderMessage, skillTagsLayout]);

  // 编辑态聚焦
  useEffect(() => {
    if (!isEdit) return;
    const surface = textareaRef.current;
    if (!surface) return;
    surface.focus();
    const length = surface.value?.length ?? 0;
    surface.setSelectionRange(length, length);
  }, [isEdit, textareaRef]);
};

export default useTextareaAutoResize;
